import { useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Printer } from "lucide-react";
import { toast } from "sonner";

// The few checkbox options this shop actually uses on the paper BPR (Gloss Varnish, Spot UV, ...).
// Stored on the job as bpr_finishing: string[] — ticking one just adds/removes its label from that list.
const FINISH_OPTIONS = ["Gloss Varnish", "Matte Varnish", "Spot UV", "Lamination", "Hot Foil", "Embossing"];

const esc = (v) => String(v ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const v_ = (v, fallback = "") => (v === null || v === undefined || v === "" ? fallback : v);

/** Row/cell helpers mirror the paper form's own grid exactly: W.O./Job Name/Product Code/Old Code
 *  across the top, then Client Name beside a stacked Order Qty/Planned Good/Waste/Excess block, etc.
 *  Used for both the on-screen view and the print window, so the two never drift apart. */
function cellsHtml(job, specialInstructions, finishing) {
  const cell = (label, value, mono) => `<div class="cell"><div class="lbl">${esc(label)}</div><div class="val${mono ? " mono" : ""}">${esc(v_(value, "—"))}</div></div>`;
  const stack = (lines) => `<div class="cell"><div class="stack">${lines.map(([l, val]) => `<div class="stack-row"><span class="stack-lbl">${esc(l)}</span><span class="mono">${esc(v_(val, "—"))}</span></div>`).join("")}</div></div>`;
  const check = (label, checked) => `<span class="chk${checked ? " on" : ""}">${checked ? "&#10003;" : ""}</span> ${esc(label)}`;

  return `
    <div class="row cols-4">
      ${cell("W.O. No", job.mpwo_number, true)}
      ${cell("Job Name", job.product_name)}
      ${cell("Product Code / Old Code", [job.product_code, job.old_code].filter(Boolean).join(" / "), true)}
      ${cell("Date", formatDate(job.bpr_issue_date) || new Date().toLocaleDateString("en-GB"))}
    </div>
    <div class="row cols-2">
      ${cell("Client Name", job.customer_name)}
      ${stack([
        ["Order Qty", job.quantity ? `${Number(job.quantity).toLocaleString("en-IN")} ${job.unit || "NOS"}` : ""],
        ["Planned Good Meter", job.planned_good_meter ? `${job.planned_good_meter} Mtrs` : ""],
        ["Allocated Waste Meter", job.planned_waste_meter ? `${job.planned_waste_meter} Mtrs` : ""],
        ["Excess Qty", job.excess_qty ? `${job.excess_qty} Mtrs` : ""],
      ])}
    </div>
    <div class="row cols-2">
      <div class="cell">
        <div class="lbl">Type of Job</div>
        <div class="val">${check((job.job_type || "New") + " Job", true)}</div>
        <div class="lbl" style="margin-top:6px">If any changes in repeat, specify</div>
        <div class="val">${esc(v_(job.bpr_repeat_changes, "NA"))}</div>
      </div>
      ${stack([
        ["Batch No", job.bpr_batch_no || "N/A"],
        ["Mfg Date", job.bpr_mfg_date ? formatDate(job.bpr_mfg_date) : "N/A"],
        ["Exp Date", job.bpr_exp_date ? formatDate(job.bpr_exp_date) : "N/A"],
        ["Price", job.unit_price ? `Rs. ${job.unit_price}` : "N/A"],
      ])}
    </div>
    <div class="row cols-1">
      <div class="cell">
        <div class="lbl">Finishing Details</div>
        <div class="val finishing">${FINISH_OPTIONS.map((opt) => check(opt, finishing.includes(opt))).join("&nbsp;&nbsp;&nbsp;")}</div>
      </div>
    </div>
    ${specialInstructions ? `<div class="row cols-1">${cell("Special Instructions, Specify", specialInstructions)}</div>` : ""}
    <div class="row cols-1">${cell("QC Master Docket No", job.qc_docket_no, true)}</div>
    <div class="row cols-1">${cell("PO No. & Date", job.po_number ? `${job.po_number}${job.po_date ? "   " + formatDate(job.po_date) : ""}` : "", true)}</div>
    <div class="row cols-1">${cell("Delivery Location", job.delivery_location)}</div>
    <div class="row cols-2">
      ${cell("Job Issue Date", job.bpr_issue_date ? formatDate(job.bpr_issue_date) : "")}
      ${cell("Job Due Date", job.due_date ? formatDate(job.due_date) : "")}
    </div>
    <div class="row cols-2">
      ${cell("BPR Issued by, (PPIC)  Date & Time", job.bpr_issued_by || job.entry_by ? `${job.bpr_issued_by || job.entry_by}  ${job.bpr_issue_date ? formatDate(job.bpr_issue_date) : ""}` : "")}
      ${cell("BPR Submitted (after completion) by, (Production)", job.bpr_submitted_by ? `${job.bpr_submitted_by}  ${job.bpr_submitted_at ? formatDate(job.bpr_submitted_at) : ""}` : "")}
    </div>
    <div class="row cols-2">
      ${cell("BPR Received by, Date & Time  (Production)", job.bpr_received_by_production ? `${job.bpr_received_by_production}  ${job.bpr_received_production_at ? formatDate(job.bpr_received_production_at) : ""}` : "")}
      ${cell("BPR Received (after completion) by, (Q.C.)", job.bpr_received_by_qc ? `${job.bpr_received_by_qc}  ${job.bpr_received_qc_at ? formatDate(job.bpr_received_qc_at) : ""}` : "")}
    </div>
  `;
}

const PRINT_CSS = `
  body { font-family: Arial, Helvetica, sans-serif; color: #111827; margin: 18mm 14mm; }
  .head { text-align: center; margin-bottom: 4mm; }
  .head .company { font-size: 16px; font-weight: 700; }
  .head .title { font-size: 12px; font-weight: 700; letter-spacing: 0.04em; margin-top: 2mm; text-transform: uppercase; }
  .table { border: 1px solid #111827; border-top: none; }
  .row { display: grid; border-top: 1px solid #111827; }
  .row.cols-4 { grid-template-columns: repeat(4, 1fr); }
  .row.cols-2 { grid-template-columns: repeat(2, 1fr); }
  .row.cols-1 { grid-template-columns: 1fr; }
  .cell { border-left: 1px solid #111827; padding: 3mm 3mm 2.5mm; }
  .row.cols-4 .cell:first-child, .row.cols-2 .cell:first-child, .row.cols-1 .cell:first-child { border-left: none; }
  .lbl { font-size: 8px; text-transform: uppercase; letter-spacing: 0.04em; color: #4b5563; margin-bottom: 1.5mm; }
  .val { font-size: 11px; }
  .val.mono, .mono { font-variant-numeric: tabular-nums; }
  .val.finishing { line-height: 2; }
  .stack-row { display: flex; justify-content: space-between; font-size: 10.5px; padding: 0.6mm 0; }
  .stack-lbl { color: #4b5563; margin-right: 4mm; }
  .chk { display: inline-flex; align-items: center; justify-content: center; width: 3.2mm; height: 3.2mm; border: 1px solid #111827; margin-right: 1.5mm; font-size: 8px; }
  .chk.on { background: #111827; color: #fff; }
  @media print { @page { size: A4; margin: 14mm; } }
`;

function printJobCard(job, specialInstructions, finishing) {
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
  win.document.write(`<!doctype html><html><head><title>Job Card — ${esc(job.mpwo_number || job.job_number)}</title>
    <meta charset="utf-8" /><style>${PRINT_CSS}</style></head><body>
    <div class="head">
      <div class="company">MedPack India Pvt. Ltd.</div>
      <div class="title">Batch Production Record &mdash; Flexo</div>
    </div>
    <div class="table">${cellsHtml(job, specialInstructions, finishing)}</div>
  </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}

function Cell({ label, value, mono, children }) {
  return (
    <div className="border-l border-neutral-900 first:border-l-0 px-3 py-2">
      <div className="text-[9px] uppercase tracking-wider text-neutral-500 mb-1">{label}</div>
      {children || <div className={`text-sm ${mono ? "font-mono-num" : ""}`}>{value || <span className="text-neutral-300">—</span>}</div>}
    </div>
  );
}

function Stack({ lines }) {
  return (
    <div className="border-l border-neutral-900 px-3 py-2">
      {lines.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between text-xs py-0.5">
          <span className="text-neutral-500">{label}</span>
          <span className="font-mono-num">{value || "—"}</span>
        </div>
      ))}
    </div>
  );
}

function Check({ label, checked }) {
  return (
    <span className="inline-flex items-center text-sm mr-5">
      <span className={`inline-flex items-center justify-center w-4 h-4 border mr-1.5 ${checked ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-400"}`}>
        {checked ? "✓" : ""}
      </span>
      {label}
    </span>
  );
}

/** Batch Production Record — Flexo, laid out exactly like the shop-floor paper BPR (same row/column
 *  grouping, same field order). Everything that already lives on the job — from Overview and Planning
 *  Spec — is pulled in automatically; the Edit dialog only covers the handful of fields unique to this
 *  record. Print opens a clean print-only window of just this record. */
export default function BatchProductionRecord({ job, onSaved }) {
  const specialInstructions = [job.spl_instruction_1, job.spl_instruction_2, job.spl_instruction_3].filter(Boolean).join(" · ");
  const finishing = job.bpr_finishing || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-center flex-1">
          <div className="text-base font-bold">MedPack India Pvt. Ltd.</div>
          <div className="text-xs font-bold uppercase tracking-wide mt-0.5">Batch Production Record — Flexo</div>
        </div>
        <EditBprRecordDialog job={job} onSaved={onSaved} />
      </div>

      <section className="border border-neutral-900 bg-white text-sm" data-testid="bpr-record-view">
        <div className="grid grid-cols-4 border-t-0">
          <Cell label="W.O. No" value={job.mpwo_number} mono />
          <Cell label="Job Name" value={job.product_name} />
          <Cell label="Product Code / Old Code" value={[job.product_code, job.old_code].filter(Boolean).join(" / ")} mono />
          <Cell label="Date" value={job.bpr_issue_date ? formatDate(job.bpr_issue_date) : new Date().toLocaleDateString("en-GB")} />
        </div>
        <div className="grid grid-cols-2 border-t border-neutral-900">
          <Cell label="Client Name" value={job.customer_name} />
          <Stack lines={[
            ["Order Qty", job.quantity ? `${job.quantity.toLocaleString("en-IN")} ${job.unit || "NOS"}` : ""],
            ["Planned Good Meter", job.planned_good_meter ? `${job.planned_good_meter} Mtrs` : ""],
            ["Allocated Waste Meter", job.planned_waste_meter ? `${job.planned_waste_meter} Mtrs` : ""],
            ["Excess Qty", job.excess_qty ? `${job.excess_qty} Mtrs` : ""],
          ]} />
        </div>
        <div className="grid grid-cols-2 border-t border-neutral-900">
          <Cell label="Type of Job">
            <div><Check label={(job.job_type || "New") + " Job"} checked /></div>
            <div className="text-[9px] uppercase tracking-wider text-neutral-500 mt-2 mb-1">If any changes in repeat, specify</div>
            <div className="text-sm">{job.bpr_repeat_changes || "NA"}</div>
          </Cell>
          <Stack lines={[
            ["Batch No", job.bpr_batch_no || "N/A"],
            ["Mfg Date", job.bpr_mfg_date ? formatDate(job.bpr_mfg_date) : "N/A"],
            ["Exp Date", job.bpr_exp_date ? formatDate(job.bpr_exp_date) : "N/A"],
            ["Price", job.unit_price ? `₹${job.unit_price}` : "N/A"],
          ]} />
        </div>
        <div className="border-t border-neutral-900">
          <Cell label="Finishing Details">
            <div className="leading-loose">{FINISH_OPTIONS.map((opt) => <Check key={opt} label={opt} checked={finishing.includes(opt)} />)}</div>
          </Cell>
        </div>
        {specialInstructions && (
          <div className="border-t border-neutral-900"><Cell label="Special Instructions, Specify" value={specialInstructions} /></div>
        )}
        <div className="border-t border-neutral-900"><Cell label="QC Master Docket No" value={job.qc_docket_no} mono /></div>
        <div className="border-t border-neutral-900">
          <Cell label="PO No. & Date" value={job.po_number ? `${job.po_number}${job.po_date ? "   " + formatDate(job.po_date) : ""}` : ""} mono />
        </div>
        <div className="border-t border-neutral-900"><Cell label="Delivery Location" value={job.delivery_location} /></div>
        <div className="grid grid-cols-2 border-t border-neutral-900">
          <Cell label="Job Issue Date" value={job.bpr_issue_date ? formatDate(job.bpr_issue_date) : ""} />
          <Cell label="Job Due Date" value={job.due_date ? formatDate(job.due_date) : ""} />
        </div>
        <div className="grid grid-cols-2 border-t border-neutral-900">
          <Cell label="BPR Issued by, (PPIC)  Date & Time" value={job.bpr_issued_by || job.entry_by ? `${job.bpr_issued_by || job.entry_by}   ${job.bpr_issue_date ? formatDate(job.bpr_issue_date) : ""}` : ""} />
          <Cell label="BPR Submitted (after completion) by, (Production)" value={job.bpr_submitted_by ? `${job.bpr_submitted_by}   ${job.bpr_submitted_at ? formatDate(job.bpr_submitted_at) : ""}` : ""} />
        </div>
        <div className="grid grid-cols-2 border-t border-neutral-900">
          <Cell label="BPR Received by, Date & Time  (Production)" value={job.bpr_received_by_production ? `${job.bpr_received_by_production}   ${job.bpr_received_production_at ? formatDate(job.bpr_received_production_at) : ""}` : ""} />
          <Cell label="BPR Received (after completion) by, (Q.C.)" value={job.bpr_received_by_qc ? `${job.bpr_received_by_qc}   ${job.bpr_received_qc_at ? formatDate(job.bpr_received_qc_at) : ""}` : ""} />
        </div>
      </section>

      <div className="flex justify-end">
        <Button type="button" variant="outline" className="rounded-sm gap-1.5" data-testid="bpr-record-print-btn"
          onClick={() => printJobCard(job, specialInstructions, finishing)}>
          <Printer size={14} /> Print
        </Button>
      </div>
    </div>
  );
}

function blankForm(job) {
  return {
    bpr_repeat_changes: job.bpr_repeat_changes || "",
    bpr_batch_no: job.bpr_batch_no || "",
    bpr_mfg_date: job.bpr_mfg_date || "",
    bpr_exp_date: job.bpr_exp_date || "",
    qc_docket_no: job.qc_docket_no || "",
    bpr_finishing: job.bpr_finishing || [],
    bpr_submitted_by: job.bpr_submitted_by || "",
    bpr_submitted_at: job.bpr_submitted_at || "",
    bpr_received_by_production: job.bpr_received_by_production || "",
    bpr_received_production_at: job.bpr_received_production_at || "",
    bpr_received_by_qc: job.bpr_received_by_qc || "",
    bpr_received_qc_at: job.bpr_received_qc_at || "",
  };
}

function F({ label, value, onChange, type = "text", mono, testId }) {
  return (
    <label className="text-xs">
      <span className="label-caps block mb-1">{label}</span>
      <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className={`rounded-sm h-9 text-sm ${mono ? "font-mono-num" : ""}`} />
    </label>
  );
}

function EditBprRecordDialog({ job, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFinish = (opt) => setForm((f) => ({
    ...f,
    bpr_finishing: f.bpr_finishing.includes(opt) ? f.bpr_finishing.filter((x) => x !== opt) : [...f.bpr_finishing, opt],
  }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/jobs/${job.id}`, form);
      toast.success("Batch Production Record updated");
      setOpen(false);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(blankForm(job)); }}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="bpr-record-edit-btn">
          <Pencil size={13} /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl rounded-sm max-h-[88vh] overflow-y-auto" data-testid="bpr-record-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Batch Production Record</DialogTitle>
          <DialogDescription>Everything else here comes from Overview and Planning Spec — edit those tabs to change it.</DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <F label="Changes in Repeat (if any)" value={form.bpr_repeat_changes} onChange={(v) => upd("bpr_repeat_changes", v)} testId="bpr-repeat_changes" />
              <F label="QC Master Docket No" value={form.qc_docket_no} onChange={(v) => upd("qc_docket_no", v)} mono testId="bpr-qc_docket_no" />
              <F label="Batch No" value={form.bpr_batch_no} onChange={(v) => upd("bpr_batch_no", v)} mono testId="bpr-batch_no" />
              <F label="Mfg Date" type="date" value={form.bpr_mfg_date} onChange={(v) => upd("bpr_mfg_date", v)} testId="bpr-mfg_date" />
              <F label="Exp Date" type="date" value={form.bpr_exp_date} onChange={(v) => upd("bpr_exp_date", v)} testId="bpr-exp_date" />
            </div>

            <div>
              <div className="label-caps text-[10px] mb-2 text-neutral-500">Finishing Details</div>
              <div className="flex flex-wrap gap-3">
                {FINISH_OPTIONS.map((opt) => (
                  <label key={opt} className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid={`bpr-finish-${opt.replace(/\s+/g, "-").toLowerCase()}`}>
                    <input type="checkbox" checked={form.bpr_finishing.includes(opt)} onChange={() => toggleFinish(opt)} />
                    {opt}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="label-caps text-[10px] mb-2 text-neutral-500">Submitted / Received (after completion)</div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Submitted by (Production)" value={form.bpr_submitted_by} onChange={(v) => upd("bpr_submitted_by", v)} testId="bpr-submitted_by" />
                <F label="Submitted Date" type="date" value={form.bpr_submitted_at} onChange={(v) => upd("bpr_submitted_at", v)} testId="bpr-submitted_at" />
                <F label="Received by (Production)" value={form.bpr_received_by_production} onChange={(v) => upd("bpr_received_by_production", v)} testId="bpr-received_by_production" />
                <F label="Received Date (Production)" type="date" value={form.bpr_received_production_at} onChange={(v) => upd("bpr_received_production_at", v)} testId="bpr-received_production_at" />
                <F label="Received by (QC)" value={form.bpr_received_by_qc} onChange={(v) => upd("bpr_received_by_qc", v)} testId="bpr-received_by_qc" />
                <F label="Received Date (QC)" type="date" value={form.bpr_received_qc_at} onChange={(v) => upd("bpr_received_qc_at", v)} testId="bpr-received_qc_at" />
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button type="button" data-testid="bpr-record-save" disabled={saving || !form} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]" onClick={save}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
